export type SportKey = "basketball" | "football" | "esports";

export type EventStatus = "live" | "upcoming" | "ended";

export interface Team {
  name: string;
  short: string;
  color: string;
}

export interface SportCategory {
  key: SportKey;
  name: string;
  enName: string;
  tagline: string;
  description: string;
  icon: string;
  accent: string;
  image: string;
}

export interface SportEvent {
  id: string;
  sport: SportKey;
  league: string;
  round: string;
  title: string;
  home: Team;
  away: Team;
  homeScore: number;
  awayScore: number;
  status: EventStatus;
  progress: string;
  time: string;
  date: string;
  region: string;
  venue: string;
  level: string;
  viewers: string;
  replay: boolean;
  featured: boolean;
  description: string;
}

export const sportCategories: SportCategory[] = [
  {
    key: "basketball",
    name: "篮球",
    enName: "Basketball",
    tagline: "NBA · CBA 全场聚焦",
    description:
      "从美职篮到本土联赛，投篮、快攻、绝杀一网打尽，实时比分与高清画质同步呈现，感受每一次起跳的心跳加速。",
    icon: "ri-basketball-line",
    accent: "primary",
    image:
      "https://readdy.ai/api/search-image?query=Dark%20cinematic%20indoor%20basketball%20arena%20with%20glossy%20hardwood%20court%20reflecting%20warm%20orange%20spotlights%2C%20deep%20black%20shadows%2C%20dramatic%20atmospheric%20haze%2C%20professional%20sports%20broadcast%20mood%2C%20high%20contrast%2C%20no%20text&width=900&height=700&seq=jrs-cat-basketball&orientation=portrait",
  },
  {
    key: "football",
    name: "足球",
    enName: "Football",
    tagline: "英超 · 西甲 · 欧冠",
    description:
      "五大联赛与洲际杯赛全覆盖，进球、助攻、战术板即时更新，陪你从开场哨响看到终场欢呼。",
    icon: "ri-football-line",
    accent: "accent",
    image:
      "https://readdy.ai/api/search-image?query=Dark%20cinematic%20football%20stadium%20at%20night%20under%20intense%20floodlights%2C%20deep%20green%20pitch%20with%20warm%20orange%20rim%20light%2C%20black%20atmospheric%20haze%2C%20empty%20stands%2C%20professional%20sports%20broadcast%20mood%2C%20high%20contrast%2C%20no%20text&width=900&height=700&seq=jrs-cat-football&orientation=portrait",
  },
  {
    key: "esports",
    name: "电竞",
    enName: "Esports",
    tagline: "LPL · KPL · CS2",
    description:
      "英雄联盟、王者荣耀、CS2 等顶尖赛事齐聚，团战高光、选手镜头、复盘数据一并奉上，燃到最后一秒。",
    icon: "ri-gamepad-line",
    accent: "secondary",
    image:
      "https://readdy.ai/api/search-image?query=Dark%20futuristic%20esports%20tournament%20stage%20with%20neon%20orange%20and%20red%20lighting%20beams%2C%20giant%20LED%20screens%2C%20deep%20black%20atmosphere%2C%20dramatic%20haze%2C%20professional%20gaming%20arena%2C%20high%20contrast%2C%20no%20text&width=900&height=700&seq=jrs-cat-esports&orientation=portrait",
  },
];

export const sportNames: Record<SportKey, string> = {
  basketball: "篮球",
  football: "足球",
  esports: "电竞",
};

export const events: SportEvent[] = [
  {
    id: "nb-20261013-lakers-warriors",
    sport: "basketball",
    league: "NBA 常规赛",
    round: "第 12 轮",
    title: "洛杉矶湖人 vs 金州勇士",
    home: { name: "洛杉矶湖人", short: "LAL", color: "#F2B705" },
    away: { name: "金州勇士", short: "GSW", color: "#F26522" },
    homeScore: 98,
    awayScore: 102,
    status: "live",
    progress: "第三节 06:24",
    time: "正在直播",
    date: "2026-10-13T20:00:00",
    region: "北美",
    venue: "Crypto.com 竞技场",
    level: "顶级职业联赛",
    viewers: "128.6 万",
    replay: false,
    featured: true,
    description:
      "洛杉矶湖人坐镇主场迎战金州勇士，两队本赛季状态火热，内线对抗与外线三分将成为本场胜负手。",
  },
  {
    id: "nb-20261013-celtics-heat",
    sport: "basketball",
    league: "NBA 常规赛",
    round: "第 12 轮",
    title: "波士顿凯尔特人 vs 迈阿密热火",
    home: { name: "波士顿凯尔特人", short: "BOS", color: "#2E7D32" },
    away: { name: "迈阿密热火", short: "MIA", color: "#C62828" },
    homeScore: 0,
    awayScore: 0,
    status: "upcoming",
    progress: "未开始",
    time: "今天 21:30",
    date: "2026-10-13T21:30:00",
    region: "北美",
    venue: "TD 花园球馆",
    level: "顶级职业联赛",
    viewers: "预告中",
    replay: false,
    featured: true,
    description:
      "东部劲旅正面对话，凯尔特人的团队传导对阵热火的强硬防守，赛前预测胜负难料。",
  },
  {
    id: "nb-20261012-nuggets-suns",
    sport: "basketball",
    league: "NBA 常规赛",
    round: "第 11 轮",
    title: "丹佛掘金 vs 菲尼克斯太阳",
    home: { name: "丹佛掘金", short: "DEN", color: "#1B3A6B" },
    away: { name: "菲尼克斯太阳", short: "PHX", color: "#E65100" },
    homeScore: 118,
    awayScore: 110,
    status: "ended",
    progress: "全场结束",
    time: "昨天 10:00",
    date: "2026-10-12T10:00:00",
    region: "北美",
    venue: "球馆中心",
    level: "顶级职业联赛",
    viewers: "96.2 万",
    replay: true,
    featured: false,
    description:
      "掘金凭借末节一波 12:2 的攻击波锁定胜局，内线核心砍下三双数据，太阳外线手感低迷。",
  },
  {
    id: "nb-20261014-bucks-76ers",
    sport: "basketball",
    league: "NBA 常规赛",
    round: "第 13 轮",
    title: "密尔沃基雄鹿 vs 费城 76 人",
    home: { name: "密尔沃基雄鹿", short: "MIL", color: "#2C5E3E" },
    away: { name: "费城 76 人", short: "PHI", color: "#1E4E8C" },
    homeScore: 0,
    awayScore: 0,
    status: "upcoming",
    progress: "未开始",
    time: "明天 08:00",
    date: "2026-10-14T08:00:00",
    region: "北美",
    venue: "论坛球馆",
    level: "顶级职业联赛",
    viewers: "预告中",
    replay: false,
    featured: false,
    description:
      "两支东部强队交手，锋线对位与挡拆效率将成为本场比赛的关键看点。",
  },
  {
    id: "cb-20261013-guangdong-liaoning",
    sport: "basketball",
    league: "CBA 常规赛",
    round: "第 8 轮",
    title: "广东宏远 vs 辽宁本钢",
    home: { name: "广东宏远", short: "GD", color: "#0D47A1" },
    away: { name: "辽宁本钢", short: "LN", color: "#B71C1C" },
    homeScore: 76,
    awayScore: 71,
    status: "live",
    progress: "第四节 08:12",
    time: "正在直播",
    date: "2026-10-13T19:35:00",
    region: "中国",
    venue: "东莞篮球中心",
    level: "国内顶级联赛",
    viewers: "82.4 万",
    replay: false,
    featured: true,
    description:
      "CBA 传统强强对话，广东与辽宁的每一次交锋都牵动人心，本场冠军底蕴对决不容错过。",
  },
  {
    id: "cb-20261012-zhejiang-shanghai",
    sport: "basketball",
    league: "CBA 常规赛",
    round: "第 7 轮",
    title: "浙江稠州 vs 上海久事",
    home: { name: "浙江稠州", short: "ZJ", color: "#00695C" },
    away: { name: "上海久事", short: "SH", color: "#4527A0" },
    homeScore: 104,
    awayScore: 97,
    status: "ended",
    progress: "全场结束",
    time: "昨天 19:35",
    date: "2026-10-12T19:35:00",
    region: "中国",
    venue: "杭州奥体中心",
    level: "国内顶级联赛",
    viewers: "54.1 万",
    replay: true,
    featured: false,
    description:
      "浙江外线全场命中 16 记三分，上海末节反扑未果，主队稳守胜果。",
  },
  {
    id: "cb-20261015-liaoning-xinjiang",
    sport: "basketball",
    league: "CBA 常规赛",
    round: "第 9 轮",
    title: "辽宁本钢 vs 新疆伊力王",
    home: { name: "辽宁本钢", short: "LN", color: "#B71C1C" },
    away: { name: "新疆伊力王", short: "XJ", color: "#00695C" },
    homeScore: 0,
    awayScore: 0,
    status: "upcoming",
    progress: "未开始",
    time: "10 月 15 日 19:35",
    date: "2026-10-15T19:35:00",
    region: "中国",
    venue: "沈阳奥体中心",
    level: "国内顶级联赛",
    viewers: "预告中",
    replay: false,
    featured: false,
    description:
      "辽宁主场迎战新疆，两队内线与外援配置接近，预计将是一场拉锯战。",
  },
  {
    id: "nb-20261011-mavericks-thunder",
    sport: "basketball",
    league: "NBA 常规赛",
    round: "第 10 轮",
    title: "达拉斯独行侠 vs 俄克拉荷马雷霆",
    home: { name: "达拉斯独行侠", short: "DAL", color: "#1565C0" },
    away: { name: "俄克拉荷马雷霆", short: "OKC", color: "#0277BD" },
    homeScore: 121,
    awayScore: 115,
    status: "ended",
    progress: "加时结束",
    time: "10 月 11 日 09:00",
    date: "2026-10-11T09:00:00",
    region: "北美",
    venue: "美航中心",
    level: "顶级职业联赛",
    viewers: "88.7 万",
    replay: true,
    featured: false,
    description:
      "双方战至加时，独行侠核心在最后 8 秒命中制胜跳投，为球迷献上经典一战。",
  },
  {
    id: "fb-20261013-city-liverpool",
    sport: "football",
    league: "英超联赛",
    round: "第 9 轮",
    title: "曼城 vs 利物浦",
    home: { name: "曼城", short: "MCI", color: "#29B6D8" },
    away: { name: "利物浦", short: "LIV", color: "#C62828" },
    homeScore: 2,
    awayScore: 1,
    status: "live",
    progress: "下半场 67'",
    time: "正在直播",
    date: "2026-10-13T22:30:00",
    region: "欧洲",
    venue: "伊蒂哈德球场",
    level: "顶级职业联赛",
    viewers: "215.3 万",
    replay: false,
    featured: true,
    description:
      "英超天王山之战，高位逼抢与快速反击的极致碰撞，一粒进球就可能改写争冠格局。",
  },
  {
    id: "fb-20261014-real-barcelona",
    sport: "football",
    league: "西甲联赛",
    round: "第 10 轮",
    title: "皇家马德里 vs 巴塞罗那",
    home: { name: "皇家马德里", short: "RMA", color: "#1A237E" },
    away: { name: "巴塞罗那", short: "BAR", color: "#7B1FA2" },
    homeScore: 0,
    awayScore: 0,
    status: "upcoming",
    progress: "未开始",
    time: "明天 03:00",
    date: "2026-10-14T03:00:00",
    region: "欧洲",
    venue: "伯纳乌球场",
    level: "顶级职业联赛",
    viewers: "预告中",
    replay: false,
    featured: true,
    description:
      "国家德比再度来袭，两队中前场配置豪华，这场比赛从来不缺少话题与火花。",
  },
  {
    id: "fb-20261012-bayern-dortmund",
    sport: "football",
    league: "德甲联赛",
    round: "第 8 轮",
    title: "拜仁慕尼黑 vs 多特蒙德",
    home: { name: "拜仁慕尼黑", short: "BAY", color: "#C62828" },
    away: { name: "多特蒙德", short: "BVB", color: "#F9A825" },
    homeScore: 3,
    awayScore: 2,
    status: "ended",
    progress: "全场结束",
    time: "昨天 00:30",
    date: "2026-10-12T00:30:00",
    region: "欧洲",
    venue: "安联球场",
    level: "顶级职业联赛",
    viewers: "143.8 万",
    replay: true,
    featured: false,
    description:
      "国家德比火力全开，五粒进球难分伯仲，拜仁补时阶段绝杀锁定三分。",
  },
  {
    id: "fb-20261015-arsenal-chelsea",
    sport: "football",
    league: "英超联赛",
    round: "第 10 轮",
    title: "阿森纳 vs 切尔西",
    home: { name: "阿森纳", short: "ARS", color: "#C62828" },
    away: { name: "切尔西", short: "CHE", color: "#1565C0" },
    homeScore: 0,
    awayScore: 0,
    status: "upcoming",
    progress: "未开始",
    time: "10 月 15 日 03:00",
    date: "2026-10-15T03:00:00",
    region: "欧洲",
    venue: "酋长球场",
    level: "顶级职业联赛",
    viewers: "预告中",
    replay: false,
    featured: false,
    description:
      "伦敦德比一触即发，阿森纳的控球体系迎战切尔西的年轻冲击力，看点十足。",
  },
  {
    id: "fb-20261013-psg-marseille",
    sport: "football",
    league: "法甲联赛",
    round: "第 9 轮",
    title: "巴黎圣日耳曼 vs 马赛",
    home: { name: "巴黎圣日耳曼", short: "PSG", color: "#283593" },
    away: { name: "马赛", short: "OM", color: "#29B6D8" },
    homeScore: 1,
    awayScore: 0,
    status: "live",
    progress: "下半场 52'",
    time: "正在直播",
    date: "2026-10-13T23:00:00",
    region: "欧洲",
    venue: "王子公园球场",
    level: "顶级职业联赛",
    viewers: "97.5 万",
    replay: false,
    featured: false,
    description:
      "法兰西德比氛围火爆，主队凭点球取得领先，客队正全力寻求扳平。",
  },
  {
    id: "fb-20261011-inter-milan",
    sport: "football",
    league: "意甲联赛",
    round: "第 8 轮",
    title: "国际米兰 vs AC 米兰",
    home: { name: "国际米兰", short: "INT", color: "#1B3A6B" },
    away: { name: "AC 米兰", short: "MIL", color: "#C62828" },
    homeScore: 2,
    awayScore: 2,
    status: "ended",
    progress: "全场结束",
    time: "10 月 11 日 02:45",
    date: "2026-10-11T02:45:00",
    region: "欧洲",
    venue: "圣西罗球场",
    level: "顶级职业联赛",
    viewers: "121.4 万",
    replay: true,
    featured: false,
    description:
      "米兰德比四球平分秋色，双方你来我往，攻防转换节奏极快，堪称意甲经典。",
  },
  {
    id: "fb-20261016-united-spurs",
    sport: "football",
    league: "英超联赛",
    round: "第 10 轮",
    title: "曼联 vs 托特纳姆热刺",
    home: { name: "曼联", short: "MUN", color: "#C62828" },
    away: { name: "托特纳姆热刺", short: "TOT", color: "#283593" },
    homeScore: 0,
    awayScore: 0,
    status: "upcoming",
    progress: "未开始",
    time: "10 月 16 日 03:00",
    date: "2026-10-16T03:00:00",
    region: "欧洲",
    venue: "老特拉福德球场",
    level: "顶级职业联赛",
    viewers: "预告中",
    replay: false,
    featured: false,
    description:
      "红魔主场迎战热刺，双方都在为欧冠资格奋力冲刺，比赛胜负影响深远。",
  },
  {
    id: "fb-20261010-barcelona-atletico",
    sport: "football",
    league: "西甲联赛",
    round: "第 9 轮",
    title: "巴塞罗那 vs 马德里竞技",
    home: { name: "巴塞罗那", short: "BAR", color: "#7B1FA2" },
    away: { name: "马德里竞技", short: "ATM", color: "#C62828" },
    homeScore: 1,
    awayScore: 1,
    status: "ended",
    progress: "全场结束",
    time: "10 月 10 日 04:00",
    date: "2026-10-10T04:00:00",
    region: "欧洲",
    venue: "奥林匹克球场",
    level: "顶级职业联赛",
    viewers: "110.9 万",
    replay: true,
    featured: false,
    description:
      "巴萨控球占优，马竞顽强扳平，两队各取一分，争冠形势依旧胶着。",
  },
  {
    id: "esp-20261013-jdg-blg",
    sport: "esports",
    league: "英雄联盟 LPL",
    round: "季后赛 半决赛",
    title: "JDG vs BLG",
    home: { name: "JDG 京东战队", short: "JDG", color: "#C62828" },
    away: { name: "BLG 哔哩哔哩", short: "BLG", color: "#1565C0" },
    homeScore: 1,
    awayScore: 2,
    status: "live",
    progress: "第 4 局进行中",
    time: "正在直播",
    date: "2026-10-13T18:00:00",
    region: "中国",
    venue: "LPL 上海主场",
    level: "顶级职业联赛",
    viewers: "176.2 万",
    replay: false,
    featured: true,
    description:
      "两支顶尖战队季后赛相遇，运营与团战细节拉满，谁先拿到赛点谁就掌握主动权。",
  },
  {
    id: "esp-20261014-t1-gen",
    sport: "esports",
    league: "英雄联盟 LCK",
    round: "决赛",
    title: "T1 vs GEN",
    home: { name: "T1", short: "T1", color: "#C62828" },
    away: { name: "GEN 韩华生命", short: "GEN", color: "#F9A825" },
    homeScore: 0,
    awayScore: 0,
    status: "upcoming",
    progress: "未开始",
    time: "明天 17:00",
    date: "2026-10-14T17:00:00",
    region: "韩国",
    venue: "首尔 LOL PARK",
    level: "顶级职业联赛",
    viewers: "预告中",
    replay: false,
    featured: true,
    description:
      "LCK 决赛巅峰对决，T1 与 GEN 的宿命之争，冠军奖杯只属于一支队伍。",
  },
  {
    id: "esp-20261013-ag-estar",
    sport: "esports",
    league: "王者荣耀 KPL",
    round: "常规赛 第 6 轮",
    title: "成都 AG 超玩会 vs 武汉 eStar",
    home: { name: "成都 AG 超玩会", short: "AG", color: "#F9A825" },
    away: { name: "武汉 eStar", short: "ES", color: "#1B3A6B" },
    homeScore: 2,
    awayScore: 1,
    status: "live",
    progress: "第 4 局进行中",
    time: "正在直播",
    date: "2026-10-13T20:00:00",
    region: "中国",
    venue: "KPL 成都主场",
    level: "国内顶级联赛",
    viewers: "132.7 万",
    replay: false,
    featured: false,
    description:
      "KPL 老牌劲旅再度交手，双方 BP 博弈与团战执行都极具观赏性。",
  },
  {
    id: "esp-20261012-g2-fnc",
    sport: "esports",
    league: "英雄联盟 LEC",
    round: "常规赛 第 7 轮",
    title: "G2 vs FNC",
    home: { name: "G2 Esports", short: "G2", color: "#455A64" },
    away: { name: "Fnatic", short: "FNC", color: "#F26522" },
    homeScore: 0,
    awayScore: 2,
    status: "ended",
    progress: "全场结束",
    time: "昨天 23:00",
    date: "2026-10-12T23:00:00",
    region: "欧洲",
    venue: "LEC 柏林演播室",
    level: "顶级职业联赛",
    viewers: "88.3 万",
    replay: true,
    featured: false,
    description:
      "FNC 直落两局横扫对手，中野联动节奏出色，G2 未能找到有效应对。",
  },
  {
    id: "esp-20261015-navi-faze",
    sport: "esports",
    league: "CS2 顶级赛事",
    round: "四强赛",
    title: "NAVI vs FaZe Clan",
    home: { name: "NAVI", short: "NAVI", color: "#F9A825" },
    away: { name: "FaZe Clan", short: "FAZE", color: "#C62828" },
    homeScore: 0,
    awayScore: 0,
    status: "upcoming",
    progress: "未开始",
    time: "10 月 15 日 21:00",
    date: "2026-10-15T21:00:00",
    region: "国际",
    venue: "科隆电子竞技馆",
    level: "国际顶级赛事",
    viewers: "预告中",
    replay: false,
    featured: false,
    description:
      "CS2 四强焦点战，两支传统豪门的枪法与战术博弈，胜负往往在细节之间。",
  },
  {
    id: "esp-20261011-edg-rng",
    sport: "esports",
    league: "英雄联盟 LPL",
    round: "季后赛 八强",
    title: "EDG vs RNG",
    home: { name: "EDG 骑士", short: "EDG", color: "#37474F" },
    away: { name: "RNG 皇族", short: "RNG", color: "#F9A825" },
    homeScore: 3,
    awayScore: 2,
    status: "ended",
    progress: "全场结束",
    time: "10 月 11 日 18:00",
    date: "2026-10-11T18:00:00",
    region: "中国",
    venue: "LPL 上海主场",
    level: "顶级职业联赛",
    viewers: "158.5 万",
    replay: true,
    featured: false,
    description:
      "打满五局的经典鏖战，EDG 在决胜局抓住关键团战机会，惊险晋级四强。",
  },
  {
    id: "esp-20261016-wolves-gk",
    sport: "esports",
    league: "王者荣耀 KPL",
    round: "常规赛 第 7 轮",
    title: "重庆狼队 vs 佛山 GK",
    home: { name: "重庆狼队", short: "WOLF", color: "#455A64" },
    away: { name: "佛山 GK", short: "GK", color: "#1B3A6B" },
    homeScore: 0,
    awayScore: 0,
    status: "upcoming",
    progress: "未开始",
    time: "10 月 16 日 20:00",
    date: "2026-10-16T20:00:00",
    region: "中国",
    venue: "KPL 重庆主场",
    level: "国内顶级联赛",
    viewers: "预告中",
    replay: false,
    featured: false,
    description:
      "狼队主场作战，对阵状态回升的佛山 GK，双方都希望拿下这场关键积分。",
  },
  {
    id: "esp-20261010-g2-t1",
    sport: "esports",
    league: "英雄联盟 MSI",
    round: "小组赛",
    title: "G2 vs T1",
    home: { name: "G2 Esports", short: "G2", color: "#455A64" },
    away: { name: "T1", short: "T1", color: "#C62828" },
    homeScore: 1,
    awayScore: 1,
    status: "ended",
    progress: "全场结束",
    time: "10 月 10 日 20:00",
    date: "2026-10-10T20:00:00",
    region: "国际",
    venue: "MSI 伦敦主赛场",
    level: "国际顶级赛事",
    viewers: "204.6 万",
    replay: true,
    featured: false,
    description:
      "东西方顶尖战队互交白卷，各取一局，为观众奉献了两场高质量的博弈。",
  },
];

export interface Collection {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  sport: SportKey;
  eventCount: number;
  tag: string;
  image: string;
  eventIds: string[];
}

export const collections: Collection[] = [
  {
    id: "col-nba-night",
    title: "NBA 焦点之夜",
    subtitle: "美职篮每日精选",
    description: "每天更新的 NBA 强强对话，实时比分与关键球集锦同步放送。",
    sport: "basketball",
    eventCount: 4,
    tag: "热门推荐",
    image:
      "https://readdy.ai/api/search-image?query=Dark%20cinematic%20basketball%20backboard%20and%20hoop%20close%20up%20with%20warm%20orange%20rim%20light%2C%20deep%20black%20gym%20atmosphere%2C%20dramatic%20haze%2C%20professional%20sports%20detail%20shot%2C%20high%20contrast%2C%20no%20text&width=800&height=560&seq=jrs-col-nba&orientation=landscape",
    eventIds: [
      "nb-20261013-lakers-warriors",
      "nb-20261013-celtics-heat",
      "nb-20261012-nuggets-suns",
      "nb-20261014-bucks-76ers",
    ],
  },
  {
    id: "col-euro-night",
    title: "欧洲豪门之夜",
    subtitle: "五大联赛全覆盖",
    description: "英超、西甲、德甲、意甲的豪门对决一网打尽，陪你熬过每一个深夜。",
    sport: "football",
    eventCount: 4,
    tag: "编辑精选",
    image:
      "https://readdy.ai/api/search-image?query=Dark%20cinematic%20football%20goal%20net%20and%20ball%20on%20green%20pitch%20at%20night%20with%20warm%20orange%20stadium%20light%2C%20deep%20black%20atmosphere%2C%20dramatic%20haze%2C%20professional%20sports%20detail%20shot%2C%20high%20contrast%2C%20no%20text&width=800&height=560&seq=jrs-col-euro&orientation=landscape",
    eventIds: [
      "fb-20261013-city-liverpool",
      "fb-20261014-real-barcelona",
      "fb-20261015-arsenal-chelsea",
      "fb-20261012-bayern-dortmund",
    ],
  },
  {
    id: "col-esports-stage",
    title: "电竞冠军之路",
    subtitle: "LPL · KPL · CS2",
    description: "顶级战队季后赛征程实录，团战高光与选手镜头一网打尽。",
    sport: "esports",
    eventCount: 4,
    tag: "新晋专题",
    image:
      "https://readdy.ai/api/search-image?query=Dark%20futuristic%20esports%20trophy%20on%20a%20glowing%20stage%20with%20orange%20and%20red%20neon%20reflections%2C%20deep%20black%20atmosphere%2C%20dramatic%20haze%2C%20professional%20gaming%20tournament%20detail%20shot%2C%20high%20contrast%2C%20no%20text&width=800&height=560&seq=jrs-col-esports&orientation=landscape",
    eventIds: [
      "esp-20261013-jdg-blg",
      "esp-20261014-t1-gen",
      "esp-20261013-ag-estar",
      "esp-20261011-edg-rng",
    ],
  },
  {
    id: "col-cba-local",
    title: "本土篮球巡礼",
    subtitle: "CBA 全场追踪",
    description: "广东、辽宁、浙江、上海等本土劲旅的每一场硬仗，尽在掌握。",
    sport: "basketball",
    eventCount: 3,
    tag: "本地热门",
    image:
      "https://readdy.ai/api/search-image?query=Dark%20cinematic%20indoor%20basketball%20court%20corner%20with%20warm%20orange%20court%20lighting%20and%20deep%20black%20stands%2C%20atmospheric%20haze%2C%20professional%20sports%20detail%20shot%2C%20high%20contrast%2C%20no%20text&width=800&height=560&seq=jrs-col-cba&orientation=landscape",
    eventIds: [
      "cb-20261013-guangdong-liaoning",
      "cb-20261012-zhejiang-shanghai",
      "cb-20261015-liaoning-xinjiang",
    ],
  },
];

export const regionOptions: Record<SportKey | "all", string[]> = {
  all: ["全部赛区", "北美", "中国", "欧洲", "韩国", "国际"],
  basketball: ["全部赛区", "北美", "中国"],
  football: ["全部赛区", "欧洲"],
  esports: ["全部赛区", "中国", "欧洲", "韩国", "国际"],
};

export function getEventById(id: string): SportEvent | undefined {
  return events.find((e) => e.id === id);
}

export function getEventsBySport(sport: SportKey): SportEvent[] {
  return events.filter((e) => e.sport === sport);
}

export function getRelatedEvents(event: SportEvent, limit = 4): SportEvent[] {
  const sameSport = events.filter(
    (e) => e.id !== event.id && e.sport === event.sport,
  );
  const teamMatch = sameSport.filter(
    (e) => e.home.short === event.home.short || e.away.short === event.away.short,
  );
  const others = sameSport.filter((e) => !teamMatch.includes(e));
  return [...teamMatch, ...others].slice(0, limit);
}

export function searchEvents(keyword: string): SportEvent[] {
  const kw = keyword.trim().toLowerCase();
  if (!kw) return [];
  return events.filter((e) => {
    const haystack = [
      e.title,
      e.league,
      e.home.name,
      e.away.name,
      e.home.short,
      e.away.short,
      sportNames[e.sport],
      e.region,
      e.venue,
    ]
      .join(" ")
      .toLowerCase();
    return haystack.includes(kw);
  });
}

export const liveEvents = events.filter((e) => e.status === "live");
export const featuredEvents = events.filter((e) => e.featured);